import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import useSWR from 'swr';
import {
  mainColors,
  secondaryColors,
  successColors,
  accentColors,
  alertColors
} from '../../theme/themeConfig';
import PartnerPortalApi from '../../api/partnerPortal';

// ─── Status config (matches platform) ────────────────────────────────────────

const STATE_CONFIG: Record<string, { bg: string; dot: string; label: string }> =
  {
    Cancelled: {
      bg: alertColors.ALERT_6,
      dot: alertColors.ALERT,
      label: 'Cancelled'
    },
    Completed: {
      bg: successColors.SUCCESS_6,
      dot: successColors.SUCCESS_7,
      label: 'Completed'
    },
    Created: {
      bg: '#F4F7FB',
      dot: secondaryColors.SECONDARY_1,
      label: 'Awaiting'
    },
    'In Progress': {
      bg: accentColors.ACCENT_6,
      dot: accentColors.ACCENT,
      label: 'In Progress'
    }
  };

const RANGE_STATUS_CONFIG: Record<
  string,
  { bg: string; bgNormal: string; txt: string }
> = {
  Completed: {
    bg: successColors.SUCCESS_6,
    bgNormal: '#C8F0DC',
    txt: successColors.SUCCESS
  },
  Created: {
    bg: '#F4F7FB',
    bgNormal: '#E2E4EA',
    txt: secondaryColors.SECONDARY
  },
  'In Progress': {
    bg: accentColors.ACCENT_6,
    bgNormal: '#B3E5E1',
    txt: accentColors.ACCENT
  },
  Mixed: {
    bg: '#FEF6E7',
    bgNormal: '#F5D3AF',
    txt: '#E79137'
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatServiceTime = (t?: string) => {
  if (!t) return '';
  const parts = t.split(':');
  if (parts.length < 2) return t;
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const getH12 = (hour: number) => {
    if (hour === 0) return 12;
    if (hour > 12) return hour - 12;
    return hour;
  };
  return `${getH12(h)}:${m} ${ampm}`;
};

const formatHoursMinutes = (decimalHours: number) => {
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const getWeekRange = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { end, start };
};

const formatRange = (start: Date, end: Date) => {
  const fmt = (d: Date) =>
    `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  return `${fmt(start)} - ${fmt(end)}`;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getDayLabel = (d: Date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${days[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}`;
};

const getEventDayStatus = (events: any[], day: Date) => {
  const dayEvents = events.filter((ev) => isSameDay(new Date(ev.start), day));
  if (dayEvents.length === 0) return null;
  const states = dayEvents.map(
    (ev) => ev.iteration?.status || ev.state || 'Created'
  );
  if (states.every((s) => s === 'Completed')) return 'Completed';
  if (states.some((s) => s === 'In Progress')) return 'In Progress';
  return 'Created';
};

const getWeekStatus = (events: any[], start: Date, end: Date) => {
  const weekEvents = events.filter((ev) => {
    const d = new Date(ev.start);
    return d >= start && d <= end;
  });
  if (weekEvents.length === 0) return 'Created';
  const states = weekEvents.map(
    (ev) => ev.iteration?.status || ev.state || 'Created'
  );
  const unique = [...new Set(states)];
  if (unique.length === 1) return unique[0];
  return 'Mixed';
};

const getGeoIcon = (approved?: boolean | null) => {
  if (approved === true) return '✓';
  if (approved === false) return '✗';
  return '—';
};

const getGeoColor = (approved?: boolean | null) => {
  if (approved === true) return successColors.SUCCESS;
  if (approved === false) return alertColors.ALERT;
  return '#7D8090';
};

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  onBack: () => void;
  projectId: string;
}

const PortalProjectDetail: React.FC<Props> = function PortalProjectDetail({
  onBack,
  projectId
}) {
  const { data: project } = useSWR(`portal-project-${projectId}`, () =>
    PartnerPortalApi.getProjectDetail(projectId)
  );

  const [openST, setOpenST] = useState<Record<string, boolean>>({});
  const [selectedWeek, setSelectedWeek] = useState<Record<string, number>>({});
  const [selectedDay, setSelectedDay] = useState<Record<string, Date | null>>(
    {}
  );
  const [taskDetail, setTaskDetail] = useState<any | null>(null);
  const [expandedDetail, setExpandedDetail] = useState(false);
  const [carouselStart, setCarouselStart] = useState<Record<string, number>>(
    {}
  );
  const VISIBLE_WEEKS = 5;

  // All events flat
  const allEventsByST = useMemo(() => {
    if (!project) return {};
    const map: Record<string, any[]> = {};
    project.serviceTimes?.forEach((st: any) => {
      const events: any[] = [];
      st.services?.forEach((svc: any) => {
        svc.tasks?.forEach((task: any) => {
          (task.events || []).forEach((ev: any) => {
            events.push({
              ...ev,
              serviceName: svc.name,
              taskName: task.name,
              resourcesAssigned: task.resourcesAssigned
            });
          });
        });
      });
      map[st.id] = events.sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
    });
    return map;
  }, [project]);

  // Build weeks per ST
  const weeksByST = useMemo(() => {
    const map: Record<string, { end: Date; id: number; start: Date }[]> = {};
    Object.entries(allEventsByST).forEach(([stId, events]) => {
      const weekMap = new Map<string, { end: Date; id: number; start: Date }>();
      let idx = 0;
      events.forEach((ev) => {
        const d = new Date(ev.start);
        const { end, start } = getWeekRange(d);
        const key = start.toISOString().slice(0, 10);
        if (!weekMap.has(key)) {
          weekMap.set(key, { end, id: idx, start });
          idx += 1;
        }
      });
      map[stId] = Array.from(weekMap.values());
    });
    return map;
  }, [allEventsByST]);

  // Auto-open first ST
  useEffect(() => {
    if (project?.serviceTimes?.length && Object.keys(openST).length === 0) {
      const first = project.serviceTimes[0];
      setOpenST({ [first.id]: true });
      const weeks = weeksByST[first.id] || [];
      if (weeks.length > 0) {
        // Find current week
        const now = new Date();
        const current = weeks.find((w) => now >= w.start && now <= w.end);
        setSelectedWeek({ [first.id]: (current || weeks[0]).id });
      }
    }
  }, [project, weeksByST]);

  if (!project) return null;

  return (
    <Box>
      {/* Back + title */}
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            color: '#0f1c2e',
            fontSize: '22px',
            fontWeight: 700
          }}
        >
          {project.leadName || project.clientName}
        </Typography>
      </Box>

      {/* "Regular Service Times" header */}
      <Box
        sx={{
          alignItems: 'center',
          background: '#F4F7FB',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          gap: 1,
          p: '12px 16px'
        }}
      >
        <i
          className="custom-icon icon-clock"
          style={{ color: secondaryColors.SECONDARY, fontSize: 14 }}
        />
        <Typography
          sx={{ color: '#0f1c2e', fontSize: '13px', fontWeight: 600 }}
        >
          Regular Service Times
        </Typography>
      </Box>

      {/* Address */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${secondaryColors.SECONDARY_6}`,
          borderTop: 0,
          p: '14px 16px'
        }}
      >
        <Typography
          sx={{ color: '#0f1c2e', fontSize: '14px', fontWeight: 500 }}
        >
          {project.address || '—'}
        </Typography>
      </Box>

      {/* Service Times */}
      {project.serviceTimes?.map((st: any, stIdx: number) => {
        const isOpen = openST[st.id] ?? false;
        const events = allEventsByST[st.id] || [];
        const weeks = weeksByST[st.id] || [];
        const activeWeekId = selectedWeek[st.id] ?? (weeks[0]?.id || 0);
        const activeWeek = weeks.find((w) => w.id === activeWeekId);

        // Days in active week
        const weekDays: Date[] = [];
        if (activeWeek) {
          for (let i = 0; i < 7; i += 1) {
            const d = new Date(activeWeek.start);
            d.setDate(d.getDate() + i);
            weekDays.push(d);
          }
        }

        const activeDay = selectedDay[st.id] || null;
        const effectiveDay =
          activeDay && weekDays.some((d) => isSameDay(d, activeDay))
            ? activeDay
            : weekDays.find((d) => getEventDayStatus(events, d) !== null) ||
              null;

        // Tasks for the selected day
        const dayTasks: any[] = [];
        if (effectiveDay) {
          st.services?.forEach((svc: any) => {
            svc.tasks?.forEach((task: any) => {
              const dayEvents = (task.events || []).filter((ev: any) =>
                isSameDay(new Date(ev.start), effectiveDay)
              );
              if (dayEvents.length > 0) {
                dayTasks.push({
                  dayEvents,
                  name: task.name,
                  resourcesAssigned: task.resourcesAssigned,
                  serviceName: svc.name
                });
              }
            });
          });
        }

        // Schedule string: days + times
        const scheduleString = `${formatServiceTime(st.startTime)} – ${formatServiceTime(st.endTime)}`;

        return (
          <Box
            key={st.id}
            sx={{
              background: '#fff',
              border: `1px solid ${secondaryColors.SECONDARY_6}`,
              borderTop: 0,
              mb: stIdx === (project.serviceTimes?.length || 1) - 1 ? 0 : 0
            }}
          >
            {/* ST Header */}
            <Box
              onClick={() =>
                setOpenST((prev) => ({ ...prev, [st.id]: !prev[st.id] }))
              }
              sx={{
                alignItems: 'center',
                borderBottom: isOpen
                  ? `1px solid ${secondaryColors.SECONDARY_6}`
                  : 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                p: '12px 16px'
              }}
            >
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Typography
                  sx={{ color: '#0f1c2e', fontSize: '13px', fontWeight: 700 }}
                >
                  Service Time #{stIdx + 1}
                </Typography>
                <Typography sx={{ color: '#7D8090', fontSize: '12px' }}>
                  · {scheduleString}
                </Typography>
              </Box>
              <Box
                sx={{
                  alignItems: 'center',
                  border: '1px solid #E2E4EA',
                  borderRadius: '6px',
                  display: 'flex',
                  height: 28,
                  justifyContent: 'center',
                  width: 28
                }}
              >
                <Typography sx={{ color: '#7D8090', fontSize: '12px' }}>
                  {isOpen ? '▲' : '▼'}
                </Typography>
              </Box>
            </Box>

            {isOpen && (
              <Box>
                {/* Week carousel */}
                <Box
                  sx={{
                    background: '#fff',
                    border: `1px solid ${secondaryColors.SECONDARY_1}`,
                    borderRadius: '8px',
                    display: 'flex',
                    m: '12px 16px',
                    overflow: 'hidden'
                  }}
                >
                  {weeks.length > VISIBLE_WEEKS && (
                    <Box
                      onClick={() => {
                        const cs = carouselStart[st.id] || 0;
                        if (cs > 0) {
                          setCarouselStart((prev) => ({
                            ...prev,
                            [st.id]: cs - 1
                          }));
                        }
                      }}
                      sx={{
                        alignItems: 'center',
                        borderRight: `1px solid ${secondaryColors.SECONDARY_1}`,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        opacity: (carouselStart[st.id] || 0) === 0 ? 0.3 : 1,
                        width: 40
                      }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>‹</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flex: 1 }}>
                    {weeks
                      .slice(
                        carouselStart[st.id] || 0,
                        (carouselStart[st.id] || 0) + VISIBLE_WEEKS
                      )
                      .map((w, wi, visible) => {
                        const isActive = w.id === activeWeekId;
                        const weekStatus = getWeekStatus(
                          events,
                          w.start,
                          w.end
                        );
                        const cfg =
                          RANGE_STATUS_CONFIG[weekStatus] ||
                          RANGE_STATUS_CONFIG.Created;
                        return (
                          <React.Fragment key={w.id}>
                            <Box
                              onClick={() => {
                                setSelectedWeek((prev) => ({
                                  ...prev,
                                  [st.id]: w.id
                                }));
                                setSelectedDay((prev) => ({
                                  ...prev,
                                  [st.id]: null
                                }));
                              }}
                              sx={{
                                '&:hover': {
                                  backgroundColor: isActive
                                    ? mainColors.MAIN
                                    : cfg.bgNormal
                                },
                                alignItems: 'center',
                                backgroundColor: isActive ? '#1a3055' : cfg.bg,
                                borderBottom: isActive
                                  ? '3px solid rgba(255,255,255,0.4)'
                                  : '3px solid transparent',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                justifyContent: 'center',
                                minWidth: 100,
                                p: '6px 4px',
                                transition: 'background-color 0.15s',
                                width: '100%'
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: isActive
                                    ? 'rgba(255,255,255,0.7)'
                                    : cfg.txt,
                                  borderRadius: '50%',
                                  height: 6,
                                  width: 6
                                }}
                              />
                              <Typography
                                sx={{
                                  color: isActive ? '#fff' : cfg.txt,
                                  fontSize: '11px',
                                  fontWeight: isActive ? 800 : 600,
                                  textAlign: 'center'
                                }}
                              >
                                {formatRange(w.start, w.end)}
                              </Typography>
                            </Box>
                            {wi < visible.length - 1 && (
                              <Divider
                                flexItem
                                orientation="vertical"
                                sx={{
                                  borderColor: secondaryColors.SECONDARY_1
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                  </Box>
                  {weeks.length > VISIBLE_WEEKS && (
                    <Box
                      onClick={() => {
                        const cs = carouselStart[st.id] || 0;
                        if (cs + VISIBLE_WEEKS < weeks.length) {
                          setCarouselStart((prev) => ({
                            ...prev,
                            [st.id]: cs + 1
                          }));
                        }
                      }}
                      sx={{
                        alignItems: 'center',
                        borderLeft: `1px solid ${secondaryColors.SECONDARY_1}`,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        opacity:
                          (carouselStart[st.id] || 0) + VISIBLE_WEEKS >=
                          weeks.length
                            ? 0.3
                            : 1,
                        width: 40
                      }}
                    >
                      <Typography sx={{ fontSize: '14px' }}>›</Typography>
                    </Box>
                  )}
                </Box>

                {/* Day bar */}
                <Box
                  sx={{
                    backgroundColor: '#FAFBFC',
                    borderTop: '1px solid #E2E4EA',
                    display: 'flex',
                    mx: '16px'
                  }}
                >
                  {weekDays.map((d, di) => {
                    const status = getEventDayStatus(events, d);
                    const isActive = effectiveDay && isSameDay(d, effectiveDay);
                    const cfg = status
                      ? STATE_CONFIG[status] || STATE_CONFIG.Created
                      : null;
                    return (
                      <Box
                        key={d.toISOString()}
                        onClick={() =>
                          status &&
                          setSelectedDay((prev) => ({
                            ...prev,
                            [st.id]: d
                          }))
                        }
                        sx={{
                          '&:hover': status
                            ? {
                                backgroundColor: cfg?.bg || '#E2E4EA'
                              }
                            : {},
                          backgroundColor: isActive
                            ? cfg?.bg || '#E2E4EA'
                            : 'transparent',
                          borderLeft: di > 0 ? '1px solid #E2E4EA' : 'none',
                          boxShadow: isActive
                            ? 'inset 0 -2px 0 #005DA2'
                            : 'none',
                          cursor: status ? 'pointer' : 'default',
                          flex: 1,
                          p: '8px',
                          textAlign: 'center',
                          transition: 'background-color 0.1s'
                        }}
                      >
                        {status && cfg && (
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              justifyContent: 'center',
                              mb: '4px'
                            }}
                          >
                            <Box
                              sx={{
                                backgroundColor: isActive
                                  ? mainColors.MAIN
                                  : cfg.dot,
                                borderRadius: '50%',
                                height: 6,
                                width: 6
                              }}
                            />
                          </Box>
                        )}
                        <Typography
                          sx={{
                            color: status
                              ? '#0f1c2e'
                              : secondaryColors.SECONDARY_1,
                            fontSize: '11px',
                            fontWeight: isActive ? 700 : 500
                          }}
                        >
                          {getDayLabel(d)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>

                {/* Legend */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    mx: '16px',
                    p: '8px 0'
                  }}
                >
                  <Typography
                    sx={{
                      color: '#7D8090',
                      fontSize: '11px'
                    }}
                  >
                    Showing:{' '}
                    {effectiveDay
                      ? effectiveDay.toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'long',
                          weekday: 'long',
                          year: 'numeric'
                        })
                      : '—'}
                  </Typography>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 0.5
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: successColors.SUCCESS_7,
                        borderRadius: '50%',
                        height: 6,
                        width: 6
                      }}
                    />
                    <Typography sx={{ color: '#7D8090', fontSize: '10px' }}>
                      Completed
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 0.5
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: accentColors.ACCENT,
                        borderRadius: '50%',
                        height: 6,
                        width: 6
                      }}
                    />
                    <Typography sx={{ color: '#7D8090', fontSize: '10px' }}>
                      In Progress / Created
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 0.5
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: secondaryColors.SECONDARY_1,
                        borderRadius: '50%',
                        height: 6,
                        width: 6
                      }}
                    />
                    <Typography sx={{ color: '#7D8090', fontSize: '10px' }}>
                      Awaiting
                    </Typography>
                  </Box>
                </Box>

                {/* Hint */}
                <Box sx={{ mx: '16px', pb: 1 }}>
                  <Typography sx={{ color: '#7D8090', fontSize: '11px' }}>
                    ⓘ Click any task row with a status to open the task detail
                    modal (technician check-in/out, photos, notes, worked time)
                  </Typography>
                </Box>

                {/* Task table */}
                {effectiveDay && dayTasks.length > 0 && (
                  <Box sx={{ mx: '16px', pb: 2 }}>
                    {/* Header row */}
                    <Box
                      sx={{
                        borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                        display: 'flex',
                        p: '8px 0'
                      }}
                    >
                      {[
                        { label: 'TASK', w: '22%' },
                        { label: 'EST.', w: '8%' },
                        { label: 'WORKED', w: '8%' },
                        { label: 'STATUS', w: '11%' },
                        { label: 'TIME IN/OUT', w: '14%' },
                        { label: 'CHECKLIST', w: '10%' },
                        { label: 'GEO', w: '6%' },
                        { label: 'IMGS', w: '7%' },
                        { label: 'NOTES', w: '7%' }
                      ].map((col) => (
                        <Typography
                          key={col.label}
                          sx={{
                            color: '#7D8090',
                            fontSize: '10px',
                            fontWeight: 600,
                            letterSpacing: '.5px',
                            textTransform: 'uppercase',
                            width: col.w
                          }}
                        >
                          {col.label}
                        </Typography>
                      ))}
                    </Box>

                    {/* Task rows */}
                    {dayTasks.map((task: any) =>
                      task.dayEvents.map((ev: any) => {
                        const iterStatus =
                          ev.iteration?.status || ev.state || 'Created';
                        const cfg =
                          STATE_CONFIG[iterStatus] || STATE_CONFIG.Created;
                        const checkIn = ev.iteration?.checkIn
                          ? new Date(ev.iteration.checkIn).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit'
                              }
                            )
                          : null;
                        const checkOut = ev.iteration?.checkOut
                          ? new Date(ev.iteration.checkOut).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit'
                              }
                            )
                          : null;

                        return (
                          <Box
                            key={ev.id}
                            onClick={() =>
                              setTaskDetail({
                                checkIn: ev.iteration?.checkIn,
                                checkOut: ev.iteration?.checkOut,
                                checklistDone: ev.iteration?.checklistDone,
                                checklistTotal: ev.iteration?.checklistTotal,
                                closedBy: ev.iteration?.closedBy,
                                closerName: ev.iteration?.closerName,
                                geofencingApproved:
                                  ev.iteration?.geofencingApproved,
                                imageCount: ev.iteration?.imageCount,
                                noteCount: ev.iteration?.noteCount,
                                scheduledOn: ev.iteration?.scheduledOn,
                                serviceName: task.serviceName,
                                startedBy: ev.iteration?.startedBy,
                                starterName: ev.iteration?.starterName,
                                state: ev.state,
                                status:
                                  ev.iteration?.status || ev.state || 'Created',
                                taskName: task.name,
                                technicianName: ev.iteration?.technicianName,
                                workedTime: ev.iteration?.workedTime
                              })
                            }
                            sx={{
                              '&:hover': {
                                backgroundColor: mainColors.MAIN_LIGHT
                              },
                              alignItems: 'center',
                              borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                              cursor: 'pointer',
                              display: 'flex',
                              p: '10px 0'
                            }}
                          >
                            <Box sx={{ width: '22%' }}>
                              <Typography
                                sx={{
                                  color: '#0f1c2e',
                                  fontSize: '13px',
                                  fontWeight: 600
                                }}
                              >
                                {task.name}
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#7D8090',
                                  fontSize: '11px'
                                }}
                              >
                                {task.resourcesAssigned || 1} technician
                              </Typography>
                            </Box>
                            <Box sx={{ width: '8%' }}>
                              <Typography
                                sx={{
                                  color: '#0f1c2e',
                                  fontSize: '12px'
                                }}
                              >
                                {ev.iteration?.scheduledOn ? '08:00' : '—'}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '8%' }}>
                              <Typography
                                sx={{
                                  color: ev.iteration?.workedTime
                                    ? '#189A90'
                                    : '#7D8090',
                                  fontSize: '12px',
                                  fontWeight: ev.iteration?.workedTime
                                    ? 700
                                    : 400
                                }}
                              >
                                {ev.iteration?.workedTime
                                  ? formatHoursMinutes(
                                      Number(ev.iteration.workedTime)
                                    )
                                  : '—'}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '11%' }}>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  background: cfg.bg,
                                  borderRadius: '20px',
                                  display: 'inline-flex',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  gap: '5px',
                                  px: '9px',
                                  py: '3px'
                                }}
                              >
                                <Box
                                  sx={{
                                    backgroundColor: cfg.dot,
                                    borderRadius: '50%',
                                    height: 6,
                                    width: 6
                                  }}
                                />
                                <span style={{ color: cfg.dot }}>
                                  {cfg.label}
                                </span>
                              </Box>
                            </Box>
                            <Box sx={{ width: '14%' }}>
                              <Typography
                                sx={{
                                  color: '#0f1c2e',
                                  fontSize: '12px'
                                }}
                              >
                                {checkIn || '—'}
                                {checkIn && checkOut ? ` / ${checkOut}` : ''}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '10%' }}>
                              <Typography
                                sx={{
                                  color:
                                    ev.iteration?.checklistTotal > 0
                                      ? accentColors.ACCENT
                                      : '#7D8090',
                                  fontSize: '12px',
                                  fontWeight:
                                    ev.iteration?.checklistTotal > 0 ? 600 : 400
                                }}
                              >
                                {ev.iteration?.checklistTotal > 0
                                  ? `${ev.iteration.checklistDone}/${ev.iteration.checklistTotal}`
                                  : '—'}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '6%' }}>
                              <Typography
                                sx={{
                                  color: getGeoColor(
                                    ev.iteration?.geofencingApproved
                                  ),
                                  fontSize: '12px'
                                }}
                              >
                                {getGeoIcon(ev.iteration?.geofencingApproved)}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '7%' }}>
                              <Typography
                                sx={{
                                  color:
                                    ev.iteration?.imageCount > 0
                                      ? '#0f1c2e'
                                      : '#7D8090',
                                  fontSize: '12px'
                                }}
                              >
                                {ev.iteration?.imageCount > 0
                                  ? ev.iteration.imageCount
                                  : '—'}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '7%' }}>
                              <Typography
                                sx={{
                                  color:
                                    ev.iteration?.noteCount > 0
                                      ? '#0f1c2e'
                                      : '#7D8090',
                                  fontSize: '12px'
                                }}
                              >
                                {ev.iteration?.noteCount > 0
                                  ? ev.iteration.noteCount
                                  : '—'}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                )}

                {effectiveDay && dayTasks.length === 0 && (
                  <Box sx={{ p: '16px', textAlign: 'center' }}>
                    <Typography sx={{ color: '#7D8090', fontSize: '12px' }}>
                      No tasks scheduled for this day.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {/* Task detail modal */}
      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => {
          setTaskDetail(null);
          setExpandedDetail(false);
        }}
        open={Boolean(taskDetail)}
      >
        {taskDetail &&
          (() => {
            const cfg = STATE_CONFIG[taskDetail.status] || STATE_CONFIG.Created;
            const checkInTime = taskDetail.checkIn
              ? new Date(taskDetail.checkIn).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  hour12: false,
                  minute: '2-digit'
                })
              : null;
            const checkOutTime = taskDetail.checkOut
              ? new Date(taskDetail.checkOut).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  hour12: false,
                  minute: '2-digit'
                })
              : null;

            return (
              <>
                {/* Teal header */}
                <Box
                  sx={{
                    background:
                      'linear-gradient(135deg, #1a8a7d 0%, #189A90 100%)',
                    color: '#fff',
                    p: '16px 20px'
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                      {taskDetail.taskName} — Details
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setTaskDetail(null);
                        setExpandedDetail(false);
                      }}
                      size="small"
                      sx={{ color: '#fff' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      mt: 0.5
                    }}
                  >
                    <Typography sx={{ fontSize: '12px', opacity: 0.85 }}>
                      Estimated{' '}
                      <strong>
                        {taskDetail.estHours || '08'} Hours and{' '}
                        {taskDetail.estMinutes || '00'} Minutes
                      </strong>
                    </Typography>
                    {taskDetail.workedTime && (
                      <Typography sx={{ fontSize: '12px', opacity: 0.85 }}>
                        Worked{' '}
                        <strong>
                          {formatHoursMinutes(Number(taskDetail.workedTime))}
                        </strong>
                      </Typography>
                    )}
                  </Box>
                </Box>

                <DialogContent sx={{ p: 0 }}>
                  {/* Table header */}
                  <Box
                    sx={{
                      borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                      display: 'flex',
                      p: '10px 20px'
                    }}
                  >
                    {[
                      { label: 'TECHNICIAN', w: '24%' },
                      { label: 'EST.', w: '10%' },
                      { label: 'STATUS / WORKED', w: '18%' },
                      { label: 'START · END', w: '18%' },
                      { label: 'CHECKLIST', w: '10%' },
                      { label: 'GEO', w: '5%' },
                      { label: 'IMGS', w: '7%' },
                      { label: 'NOTES', w: '8%' }
                    ].map((col) => (
                      <Typography
                        key={col.label}
                        sx={{
                          color: '#7D8090',
                          fontSize: '10px',
                          fontWeight: 600,
                          letterSpacing: '.5px',
                          textTransform: 'uppercase',
                          width: col.w
                        }}
                      >
                        {col.label}
                      </Typography>
                    ))}
                  </Box>

                  {/* Technician row */}
                  <Box
                    onClick={() => setExpandedDetail(!expandedDetail)}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#FAFBFC'
                      },
                      alignItems: 'center',
                      borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                      cursor: 'pointer',
                      display: 'flex',
                      p: '12px 20px'
                    }}
                  >
                    {/* TECHNICIAN */}
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        width: '24%'
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: mainColors.MAIN,
                          fontSize: '11px',
                          fontWeight: 700,
                          height: 32,
                          width: 32
                        }}
                      >
                        {(taskDetail.technicianName || 'P')
                          .split(' ')
                          .slice(0, 2)
                          .map((w: string) => w[0])
                          .join('')
                          .toUpperCase()}
                      </Avatar>
                      <Typography
                        sx={{
                          color: '#0f1c2e',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                      >
                        {taskDetail.technicianName || 'Partner'}
                      </Typography>
                    </Box>
                    {/* EST */}
                    <Typography
                      sx={{
                        color: '#0f1c2e',
                        fontSize: '12px',
                        width: '10%'
                      }}
                    >
                      08:00
                    </Typography>
                    {/* STATUS / WORKED */}
                    <Box sx={{ width: '18%' }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 1
                        }}
                      >
                        <Box
                          sx={{
                            alignItems: 'center',
                            background: cfg.bg,
                            borderRadius: '16px',
                            display: 'inline-flex',
                            fontSize: '11px',
                            fontWeight: 600,
                            gap: '4px',
                            px: '8px',
                            py: '2px'
                          }}
                        >
                          <span style={{ color: cfg.dot }}>
                            {taskDetail.workedTime
                              ? formatHoursMinutes(
                                  Number(taskDetail.workedTime)
                                )
                              : cfg.label}
                          </span>
                        </Box>
                      </Box>
                    </Box>
                    {/* START · END */}
                    <Box sx={{ width: '18%' }}>
                      <Typography sx={{ fontSize: '12px' }}>
                        {checkInTime ? `Start: ${checkInTime}` : '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '12px' }}>
                        {checkOutTime ? `End: ${checkOutTime}` : ''}
                      </Typography>
                    </Box>
                    {/* CHECKLIST */}
                    <Typography
                      sx={{
                        color:
                          taskDetail.checklistTotal > 0
                            ? accentColors.ACCENT
                            : '#7D8090',
                        fontSize: '12px',
                        fontWeight: taskDetail.checklistTotal > 0 ? 600 : 400,
                        width: '10%'
                      }}
                    >
                      {taskDetail.checklistTotal > 0
                        ? `${taskDetail.checklistDone}/${taskDetail.checklistTotal}`
                        : '—'}
                    </Typography>
                    {/* GEO */}
                    <Typography
                      sx={{
                        color: getGeoColor(taskDetail.geofencingApproved),
                        fontSize: '12px',
                        width: '5%'
                      }}
                    >
                      {getGeoIcon(taskDetail.geofencingApproved)}
                    </Typography>
                    {/* IMGS */}
                    <Typography
                      sx={{
                        color:
                          taskDetail.imageCount > 0 ? '#0f1c2e' : '#7D8090',
                        fontSize: '12px',
                        width: '7%'
                      }}
                    >
                      {taskDetail.imageCount > 0 ? taskDetail.imageCount : '—'}
                    </Typography>
                    {/* NOTES */}
                    <Typography
                      sx={{
                        color: taskDetail.noteCount > 0 ? '#0f1c2e' : '#7D8090',
                        fontSize: '12px',
                        width: '7%'
                      }}
                    >
                      {taskDetail.noteCount > 0 ? taskDetail.noteCount : '—'}
                    </Typography>
                    {/* Expand arrow */}
                    <Typography
                      sx={{
                        color: '#7D8090',
                        fontSize: '12px',
                        width: '1%'
                      }}
                    >
                      {expandedDetail ? '▲' : '▼'}
                    </Typography>
                  </Box>

                  {/* Expanded audit trail */}
                  <Collapse in={expandedDetail}>
                    <Box
                      sx={{
                        backgroundColor: '#FAFBFC',
                        borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
                        display: 'flex',
                        gap: 4,
                        p: '16px 20px'
                      }}
                    >
                      {/* Audit trail */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            color: '#7D8090',
                            fontSize: '11px',
                            fontWeight: 600,
                            letterSpacing: '.5px',
                            mb: 1.5,
                            textTransform: 'uppercase'
                          }}
                        >
                          Audit trail
                        </Typography>
                        <Box
                          sx={{
                            background: '#fff',
                            border: `1px solid ${secondaryColors.SECONDARY_6}`,
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                        >
                          {[
                            {
                              label: 'Start time',
                              value: checkInTime || '—'
                            },
                            {
                              label: 'End time',
                              value: checkOutTime || '—'
                            },
                            {
                              label: 'Started by',
                              value: taskDetail.startedBy || '—'
                            },
                            {
                              label: 'Starter',
                              value: taskDetail.starterName || '—'
                            },
                            {
                              label: 'Closed by',
                              value: taskDetail.closedBy || '—'
                            },
                            {
                              label: 'Closer',
                              value: taskDetail.closerName || '—'
                            }
                          ].map((row, i) => (
                            <Box
                              key={row.label}
                              sx={{
                                borderTop:
                                  i > 0
                                    ? `1px solid ${secondaryColors.SECONDARY_6}`
                                    : 'none',
                                display: 'flex',
                                justifyContent: 'space-between',
                                p: '8px 12px'
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#7D8090',
                                  fontSize: '12px'
                                }}
                              >
                                {row.label}
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#0f1c2e',
                                  fontSize: '12px',
                                  fontWeight: 500
                                }}
                              >
                                {row.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </DialogContent>
              </>
            );
          })()}
      </Dialog>
    </Box>
  );
};

export default PortalProjectDetail;
